/**
 * @file useConsentTextsSource.ts
 * @description С чего начинается форма новой редакции (phase19.16, Р3г;
 * uiux-r3g-consent-texts §1, §3): действующая редакция с сервера, основа
 * («Взять за основу» ред. N) и черновик этого браузера («Продолжить»).
 *
 * `expectedCurrent` — номер действующей редакции на момент начала правки:
 * у черновика — тот, что был при его сохранении (если с тех пор вышла
 * новая, сервер ответит 409 и форма покажет конфликт). `live` — сама эта
 * редакция: от неё окно публикации считает, кого правка коснётся (ревью В1).
 *
 * Состояние хранится вместе с ключом запроса (`{ key, value }`): при смене
 * `?kind=` или основы до ответа сервера отдаётся «загрузка», а не тексты
 * прежнего вида (ревью М1).
 */

import type { ConsentBannerTexts, ConsentNoticeRiskTexts, ConsentTextKind } from "@chaiberi/shared";
import { useCallback, useEffect, useState } from "react";

import { consentTextsApi, type ConsentTextRevision } from "../../lib/api-consent-texts";
import { loadConsentDraft } from "../../lib/platform/consent-texts-draft";

/** Тексты любого вида. */
export type ConsentAnyTexts = ConsentBannerTexts | ConsentNoticeRiskTexts;

/** Тексты баннера? (у окна о риске фраз режима нет). */
export function isBannerTexts(texts: ConsentAnyTexts): texts is ConsentBannerTexts {
  return "consentPhrase" in texts;
}

export interface ConsentEditStart {
  texts: ConsentAnyTexts;
  expectedCurrent: number;
  /** Редакция-основа — от неё «Отличия от ред. N». */
  base: ConsentTextRevision;
  /** Действующая редакция с номером `expectedCurrent` — от неё «кого коснётся». */
  live: ConsentTextRevision;
  /** Форма открыта из черновика: когда он сохранён (ISO). */
  draftSavedAt: string | null;
}

export type ConsentSourceState =
  | { status: "loading" }
  | { status: "error"; message: string }
  /** Редакций нет — сид не прошёл, начинать не от чего. */
  | { status: "empty" }
  | { status: "ready"; current: ConsentTextRevision; start: ConsentEditStart };

interface Options {
  kind: ConsentTextKind;
  /** «Взять за основу» ред. N; null — действующая. */
  baseNumber: number | null;
  /** Открыть черновик браузера, если он есть. */
  fromDraft: boolean;
}

const LOAD_ERROR = "Не удалось загрузить тексты. Сайты магазинов работают со своими опубликованными текстами.";

async function loadStart({ kind, baseNumber, fromDraft }: Options): Promise<ConsentSourceState> {
  const history = await consentTextsApi.list(kind);
  if (history.length === 0) return { status: "empty" };
  const current = await consentTextsApi.current(kind);
  const draft = fromDraft ? loadConsentDraft(kind) : null;
  const byNumber = (n: number) => (n === current.number ? Promise.resolve(current) : consentTextsApi.get(kind, n));
  const base = await byNumber(draft?.baseNumber ?? baseNumber ?? current.number);
  if (draft) {
    const live = draft.expectedCurrent === base.number ? base : await byNumber(draft.expectedCurrent);
    return {
      status: "ready",
      current,
      start: { texts: draft.texts, expectedCurrent: draft.expectedCurrent, base, live, draftSavedAt: draft.savedAt },
    };
  }
  return {
    status: "ready",
    current,
    start: { texts: base.texts, expectedCurrent: current.number, base, live: current, draftSavedAt: null },
  };
}

export function useConsentTextsSource(options: Options): ConsentSourceState & { reload: () => void } {
  const { kind, baseNumber, fromDraft } = options;
  const [nonce, setNonce] = useState(0);
  const key = `${kind}|${baseNumber ?? ""}|${fromDraft ? 1 : 0}|${nonce}`;
  const [loaded, setLoaded] = useState<{ key: string; value: ConsentSourceState } | null>(null);

  useEffect(() => {
    let alive = true;
    loadStart({ kind, baseNumber, fromDraft })
      .catch((): ConsentSourceState => ({ status: "error", message: LOAD_ERROR }))
      .then((value) => {
        if (alive) setLoaded({ key, value });
      });
    return () => {
      alive = false;
    };
  }, [kind, baseNumber, fromDraft, key]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  // Ответ на другой запрос (смена вида или основы) — ещё загрузка.
  const state: ConsentSourceState = loaded?.key === key ? loaded.value : { status: "loading" };
  return { ...state, reload };
}
